/**
 * sessionManager.js
 *
 * Session lifecycle tracking for CUSTOM LOGIN users only.
 * AAD and SAML2/hybrid users are intentionally excluded.
 *
 * Flow
 * ────
 * After custom login token is stored  → startTrackedSession()
 * On any logout / page kill           → endTrackedSessionOnPageExit()
 *
 * Public API
 * ──────────
 * startTrackedSession({ force })      POST /StartSession; stores returned SessionId
 * endTrackedSessionOnPageExit()       Fire-and-forget keepalive POST /EndSession
 * getTrackedSessionId()               Read current SessionId from localStorage
 * clearTrackedSession()               Remove session keys (no network call)
 */

import { START_SESSION_API_URL, END_SESSION_API_URL } from "./apiConstants";

// ─── storage keys ─────────────────────────────────────────────────────────────
const SESSION_ID_KEY              = "activeSessionId";
const SESSION_START_IN_PROGRESS   = "_ssip";
const SESSION_END_IN_PROGRESS     = "_seip";

// ─── possible field names the backend may use for the session ID ───────────────
const SESSION_ID_FIELDS = [
  "SessionId", "SessionID", "sessionId", "sessionID",
  "Id", "ID", "id",
];

// Clear any stale "in-progress" locks left over from a previous tab that was
// killed mid-flight. Without this, a crashed end-session attempt could leave
// `_seip = "1"` in localStorage and every subsequent end attempt would be
// short-circuited as "already in progress".
try {
  if (typeof localStorage !== "undefined") {
    localStorage.removeItem(SESSION_START_IN_PROGRESS);
    localStorage.removeItem(SESSION_END_IN_PROGRESS);
  }
} catch (_) {}

// ─── internal helpers ─────────────────────────────────────────────────────────

const buildHeaders = () => {
  const token           = localStorage.getItem("token");
  const tenantName      = localStorage.getItem("tenantName");
  const securityProvider = localStorage.getItem("SecurityProviderName");
  if (!token || !tenantName) return null;
  // AAD uses "Authorization"; all other providers use "AuthenticationToken"
  const authKey = securityProvider === "aad" ? "Authorization" : "AuthenticationToken";
  return {
    "Content-Type": "application/json",
    TenantSlugName: tenantName,
    [authKey]:      token,
  };
};

const extractSessionId = (data) => {
  if (!data || typeof data !== "object") return null;
  for (const key of SESSION_ID_FIELDS) {
    if (data[key]) return String(data[key]);
  }
  return null;
};

/**
 * Low-level fetch wrapper (native fetch, not axios).
 * keepalive=true means the browser sends the request even after the page unloads.
 */
const sessionFetch = async (fullUrl, payload, keepalive = false) => {
  const headers = buildHeaders();
  if (!headers) throw new Error("sessionFetch: missing auth headers");

  const res = await fetch(fullUrl, {
    method:    "POST",
    headers,
    body:      JSON.stringify(payload || {}),
    keepalive,
  });

  if (!res.ok) throw new Error(`sessionFetch ${res.status}`);
  if (res.status === 204) return null;

  const ct = res.headers.get("content-type") || "";
  if (!ct.includes("application/json")) return null;
  return res.json();
};

// ─── public API ───────────────────────────────────────────────────────────────

export const getTrackedSessionId = () => localStorage.getItem(SESSION_ID_KEY);

/** Remove session tracking keys (no network call). */
export const clearTrackedSession = () => {
  localStorage.removeItem(SESSION_ID_KEY);
  localStorage.removeItem(SESSION_START_IN_PROGRESS);
  localStorage.removeItem(SESSION_END_IN_PROGRESS);
};

/**
 * Start a backend session after the custom-login token has been stored.
 * Idempotent — pass force:true to replace an existing session (re-login).
 * Body is intentionally empty; server derives context from auth headers.
 *
 * @param  {object}  [opts]
 * @param  {boolean} [opts.force=false]
 * @returns {Promise<string|null>}  resolved SessionId or null on failure
 */
export const startTrackedSession = async ({ force = false } = {}) => {
  const existing = getTrackedSessionId();
  if (existing && !force) return existing;

  if (localStorage.getItem(SESSION_START_IN_PROGRESS) === "1") return existing;
  localStorage.setItem(SESSION_START_IN_PROGRESS, "1");

  try {
    const data      = await sessionFetch(START_SESSION_API_URL, {});
    const sessionId = extractSessionId(data);

    if (sessionId) {
      localStorage.setItem(SESSION_ID_KEY, sessionId);
      localStorage.removeItem(SESSION_END_IN_PROGRESS);
    }

    return sessionId;
  } catch (err) {
    console.error("[sessionManager] startTrackedSession failed:", err);
    return null;
  } finally {
    localStorage.removeItem(SESSION_START_IN_PROGRESS);
  }
};

/**
 * End the current session.
 * Safe to call when no session is being tracked (becomes a no-op).
 *
 * @param  {boolean} [keepalive=false]  set true to survive page navigation/unload
 * @returns {Promise<boolean>}
 */
const endTrackedSession = async (keepalive = false) => {
  const sessionId = getTrackedSessionId();
  if (!sessionId) { clearTrackedSession(); return false; }

  if (localStorage.getItem(SESSION_END_IN_PROGRESS) === "1") return false;
  localStorage.setItem(SESSION_END_IN_PROGRESS, "1");

  try {
    // ─── DIAGNOSTIC: mark right before the fetch is dispatched ─────────────
    try {
      localStorage.setItem(
        "_endSessionDebug_dispatch",
        JSON.stringify({
          ts: new Date().toISOString(),
          sessionId,
          keepalive,
        })
      );
    } catch (_) {}

    await sessionFetch(
      END_SESSION_API_URL,
      { SessionId: sessionId },
      keepalive
    );

    try {
      localStorage.setItem(
        "_endSessionDebug_result",
        JSON.stringify({ ts: new Date().toISOString(), result: "FETCH_RESOLVED_OK" })
      );
    } catch (_) {}
    return true;
  } catch (err) {
    console.error("[sessionManager] endTrackedSession failed:", err);
    try {
      localStorage.setItem(
        "_endSessionDebug_result",
        JSON.stringify({
          ts: new Date().toISOString(),
          result: "FETCH_THREW",
          error: String(err && err.message ? err.message : err),
        })
      );
    } catch (_) {}
    return false;
  } finally {
    clearTrackedSession();
  }
};

/**
 * Send EndSession via navigator.sendBeacon.
 *
 * Why a beacon and not fetch+keepalive?
 *   The regular /EndSession POST carries custom headers (AuthenticationToken
 *   / Authorization, TenantSlugName) which force a CORS preflight (OPTIONS).
 *   On page unload (tab close / browser kill) the preflight is frequently
 *   cancelled by the browser, so the actual POST never reaches the server.
 *
 *   sendBeacon with a Blob of type "text/plain" is CORS-safelisted, so NO
 *   preflight is issued and the request is queued by the browser even after
 *   the document is gone. To compensate for the missing headers, auth is
 *   embedded in the JSON body and the server reads it from there.
 *
 * Returns true when the browser accepted the beacon into its queue.
 */
const sendEndSessionBeacon = (sessionId) => {
  if (typeof navigator === "undefined" || typeof navigator.sendBeacon !== "function") {
    return false;
  }
  try {
    const token            = localStorage.getItem("token");
    const tenantName       = localStorage.getItem("tenantName");
    const securityProvider = localStorage.getItem("SecurityProviderName");
    if (!token || !tenantName) return false;

    const payload = JSON.stringify({
      SessionId:            sessionId,
      Token:                token,
      TenantSlugName:       tenantName,
      SecurityProviderName: securityProvider,
    });

    // text/plain → CORS-safelisted → no preflight
    const blob = new Blob([payload], { type: "text/plain;charset=UTF-8" });
    return navigator.sendBeacon(END_SESSION_API_URL, blob);
  } catch (_) {
    return false;
  }
};

/**
 * Best-effort session termination for logout / browser-kill / tab-close.
 *
 * Strategy:
 *   1. Primary: navigator.sendBeacon (no CORS preflight, survives unload).
 *   2. Fallback: fetch + keepalive (works for logout/redirect cases where
 *      the document is still alive long enough for a preflight to complete).
 *
 * IMPORTANT: Call this BEFORE localStorage.clear() so the token / tenant
 * can still be read for the body.
 *
 * This is a no-op when no SessionId is stored (i.e. non-custom-login users).
 */
export const endTrackedSessionOnPageExit = () => {
  // ─── DIAGNOSTIC: write a breadcrumb to localStorage so we can verify the
  // pagehide → EndSession path even when DevTools dies with the tab. ────────
  try {
    const debug = {
      ts: new Date().toISOString(),
      sessionId: getTrackedSessionId(),
      hasToken: !!localStorage.getItem("token"),
      hasTenant: !!localStorage.getItem("tenantName"),
      provider: localStorage.getItem("SecurityProviderName"),
      endpoint: END_SESSION_API_URL,
    };
    localStorage.setItem("_endSessionDebug", JSON.stringify(debug));
  } catch (_) {}

  const sessionId = getTrackedSessionId();
  if (!sessionId) {
    try {
      localStorage.setItem(
        "_endSessionDebug_result",
        JSON.stringify({ ts: new Date().toISOString(), result: "NO_SESSION_ID_SKIPPED" })
      );
    } catch (_) {}
    return;
  }

  // Guard against re-entry (e.g. pagehide firing twice, or axios 401 racing
  // with pagehide). The lock is also set inside endTrackedSession() for the
  // fetch path; setting it here means the fallback fetch won't duplicate.
  if (localStorage.getItem(SESSION_END_IN_PROGRESS) === "1") {
    try {
      localStorage.setItem(
        "_endSessionDebug_result",
        JSON.stringify({ ts: new Date().toISOString(), result: "ALREADY_IN_PROGRESS" })
      );
    } catch (_) {}
    return;
  }
  localStorage.setItem(SESSION_END_IN_PROGRESS, "1");

  // 1️⃣ Primary path — sendBeacon (no preflight, queued by the browser).
  const beaconQueued = sendEndSessionBeacon(sessionId);

  try {
    localStorage.setItem(
      "_endSessionDebug_dispatch",
      JSON.stringify({
        ts: new Date().toISOString(),
        sessionId,
        transport: beaconQueued ? "sendBeacon" : "fetch-keepalive-fallback",
      })
    );
  } catch (_) {}

  // 2️⃣ Secondary path — always also attempt fetch+keepalive with auth headers.
  // In some environments beacon is accepted by the browser queue but rejected
  // by backend auth middleware because custom headers are unavailable.
  try {
    localStorage.setItem(
      "_endSessionDebug_result",
      JSON.stringify({
        ts: new Date().toISOString(),
        result: beaconQueued
          ? "BEACON_QUEUED_FETCH_KEEPALIVE_STARTED"
          : "BEACON_NOT_QUEUED_FETCH_KEEPALIVE_STARTED",
      })
    );
  } catch (_) {}

  // Release the lock first because endTrackedSession() sets its own lock.
  localStorage.removeItem(SESSION_END_IN_PROGRESS);
  endTrackedSession(true);
};

/**
 * Awaitable session termination — use when you can wait for the call to
 * complete before navigating (AAD logoutRedirect, SAML window.location.href).
 * Keepalive is not needed because the page is still alive until this resolves.
 */
export const endTrackedSessionNow = () => endTrackedSession(false);
